import "./org360.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useOrg360PanelState } from "@/hooks/org360";
import { useOrg360GraphData, type Org360IncludeFlags } from "@/hooks/org360/useOrg360GraphData";
import { useOrg360MetadataItems } from "@/hooks/org360/useOrg360MetadataItems";
import { OrgBadge } from "./OrgBadge";
import { GraphDock } from "./GraphDock";
import { OrgContextPanel } from "./OrgContextPanel";
import { OrgInspectorPanel } from "./OrgInspectorPanel";
import { FieldSecurityOverlay } from "./FieldSecurityOverlay";
import { Org360Graph } from "./Org360Graph";
import type { Org360Node } from "./org360Types";

export function Org360Layout() {
  const user = useAuthStoreV1((state) => state.user);
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const location = useLocation();

  const {
    contextOpen,
    inspectorOpen,
    toggleContext,
    toggleInspector,
    closeContext,
    closeInspector,
    openInspector,
  } = useOrg360PanelState();

  const [selectedType, setSelectedType] = useState<string | undefined>();
  const [selectedNode, setSelectedNode] = useState<Org360Node | null>(null);
  const [navigateToNodeId, setNavigateToNodeId] = useState<string | null>(null);
  const [hoveredRelationNodeId, setHoveredRelationNodeId] = useState<string | null>(null);
  const [graphHoveredNodeId, setGraphHoveredNodeId] = useState<string | null>(null);
  const [hiddenEdgeTypes, setHiddenEdgeTypes] = useState<Set<string>>(new Set());
  // Fields / reports / managed nodes are excluded from every view by default;
  // each can be toggled back on via the graph dock.
  const [includeFlags, setIncludeFlags] = useState<Org360IncludeFlags>({});
  const toggleInclude = useCallback((k: keyof Org360IncludeFlags) => {
    setIncludeFlags((prev) => ({ ...prev, [k]: !prev[k] }));
  }, []);
  const [fieldSecurityOpen, setFieldSecurityOpen] = useState(false);
  const [fieldSecurityObject, setFieldSecurityObject] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const OBJECT_TYPES = new Set(["CustomObject", "StandardObject"]);

  // When overlay is open and user clicks a compatible node, auto-update the object
  useEffect(() => {
    if (!fieldSecurityOpen) return;
    if (selectedNode && OBJECT_TYPES.has(selectedNode.type)) {
      setFieldSecurityObject(selectedNode.label);
    }
  }, [selectedNode, fieldSecurityOpen]);


  const handleGraphNodeHover = useCallback((id: string | null) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setGraphHoveredNodeId(id), 250);
  }, []);

  // Read optional ?node= deep-link param
  const deepLinkNode = new URLSearchParams(location.search).get("node") ?? undefined;

  // Always fetch the UNFILTERED type list for the context panel rail — the
  // filtered graph fetch is handled inside Org360Graph itself.
  const { data: allGraphData } = useOrg360GraphData(undefined);

  // Item list for the selected type — sourced from Neo4j directly (not the
  // degree-pruned graph payload), so low-connectivity types list correctly.
  const { data: typeItems, isLoading: itemsLoading } = useOrg360MetadataItems(selectedType);

  // Managed-package nodes revealed on the canvas (reported by Org360Graph) are
  // merged in so the inspector treats them as in-graph — keeping panels in sync.
  const [revealedIds, setRevealedIds] = useState<string[]>([]);
  const handleRevealedIdsChange = useCallback((ids: string[]) => setRevealedIds(ids), []);

  const graphNodeIds = useMemo(
    () => new Set([...(allGraphData?.nodes ?? []).map((n) => n.id), ...revealedIds]),
    [allGraphData?.nodes, revealedIds],
  );

  const handleNodeSelect = (node: Org360Node | null) => {
    setSelectedNode(node);
    if (node) {
      if (contextOpen) closeContext();
      openInspector();
    }
  };

  // Called from the context panel table — also pans the graph to the node
  // so the user can see exactly where it lives before the context panel closes.
  const handleContextNodeClick = useCallback((node: Org360Node) => {
    setSelectedNode(node);
    setNavigateToNodeId(node.id);
    closeContext();
    openInspector();
  }, [closeContext, openInspector]);

  const handleToggleEdgeType = useCallback((key: string) => {
    setHiddenEdgeTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setSelectedNode(null);
    setSelectedType(undefined);
    setNavigateToNodeId(null);
    setHoveredRelationNodeId(null);
    setGraphHoveredNodeId(null);
    setHiddenEdgeTypes(new Set());
    setIncludeFlags({});
    closeContext();
    closeInspector();
  }, [closeContext, closeInspector]);

  const handleNavigate = (node: { id: string; label: string; type: string }) => {
    setSelectedNode(node as Org360Node);
    setNavigateToNodeId(node.id);
    openInspector();
  };

  // Clear selected node when type filter changes
  useEffect(() => {
    setSelectedNode(null);
  }, [selectedType]);

  if (!user?.id || !selectedOrg?.id) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="max-w-sm p-6 rounded-lg border border-border bg-card text-sm text-muted-foreground text-center">
          Select an org before opening Org 360. The graph view uses the signed-in
          user and selected org from nCoder AI.
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden rounded-lg border border-border org360-canvas">
      <Org360Graph
        selectedType={selectedType}
        selectedNodeId={selectedNode?.id ?? null}
        navigateToNodeId={navigateToNodeId}
        hoveredRelationNodeId={hoveredRelationNodeId}
        deepLinkNode={deepLinkNode}
        hiddenEdgeTypes={hiddenEdgeTypes}
        includeFlags={includeFlags}
        onNodeSelect={handleNodeSelect}
        onNodeHover={handleGraphNodeHover}
        onReset={handleReset}
        onCloseAll={() => { closeContext(); closeInspector(); }}
        onRevealedIdsChange={handleRevealedIdsChange}
      />

      <OrgBadge org={selectedOrg} />

      <GraphDock
        contextOpen={contextOpen}
        inspectorOpen={inspectorOpen}
        onContext={toggleContext}
        onInspector={toggleInspector}
        hiddenEdgeTypes={hiddenEdgeTypes}
        onToggleEdgeType={handleToggleEdgeType}
        includeFlags={includeFlags}
        onToggleInclude={toggleInclude}
      />

      <OrgContextPanel
        open={contextOpen}
        onClose={closeContext}
        org={selectedOrg}
        metadataTypes={allGraphData?.metadata_types ?? []}
        selectedType={selectedType}
        onTypeSelect={setSelectedType}
        items={typeItems ?? []}
        itemsLoading={itemsLoading}
        onNodeClick={handleContextNodeClick}
        onFieldSecurity={(name) => { setFieldSecurityObject(name); setFieldSecurityOpen(true); }}
      />

      <FieldSecurityOverlay
        open={fieldSecurityOpen}
        objectName={fieldSecurityObject}
        onClose={() => setFieldSecurityOpen(false)}
      />

      <OrgInspectorPanel
        open={inspectorOpen}
        onClose={closeInspector}
        selectedNode={selectedNode}
        onNavigate={handleNavigate}
        onHoverNode={setHoveredRelationNodeId}
        graphNodeIds={graphNodeIds}
        graphHoveredNodeId={graphHoveredNodeId}
        includeFlags={includeFlags}
      />
    </div>
  );
}
